import { useCallback, useEffect, type ReactNode } from 'react';
import { Button, Result, Tabs } from 'antd';
import { AsyncBoundary } from './AsyncBoundary';
import { PageHeader, type Breadcrumb } from './PageHeader';
import { useConfirmAction } from './useConfirmAction';
import { useCapability, useCapabilityContext } from './capability/CapabilityContext';
import type { Capability } from './capability/CapabilityContext';
import { space } from './tokens';

/**
 * Shared detail-page shell: async states, a read/edit toggle, and the entire
 * unsaved-changes discard guard. Framework- and router-agnostic — the record,
 * the read view, and the edit form are injected; navigation flows through
 * `onBack`/`onNavigate` callbacks so this component can intercept them to guard
 * unsaved edits.
 *
 * The guard lives ONLY here (never Refine's UnsavedChangesNotifier): while
 * `editing && dirty`, Cancel / back / a breadcrumb each route through one
 * confirm, and a `beforeunload` listener covers a true browser navigation. The
 * injected edit form must therefore set `warnWhenUnsavedChanges: false`.
 *
 * Render precedence: loading > notFound > error > read/edit view. `loading`
 * is checked ahead of `notFound` (a caller can be re-fetching a record it
 * already knows is missing, e.g. after a retry) so the branches below are
 * kept mutually exclusive rather than delegated to a single AsyncBoundary
 * call — AsyncBoundary's own internal precedence is error > loading, which
 * would invert the `loading > notFound > error` order this component owes
 * its callers if both flags were fed into one call.
 *
 * Tab region (all three props optional, additive-by-omission): when `tabs`
 * is absent, the read branch renders `readView` exactly as before — every
 * existing consumer that doesn't pass `tabs` is byte-for-byte unchanged.
 * When `tabs` is present, the read branch renders an antd `<Tabs>` built
 * from `tabs`, filtered by capability (a `cap`-less tab always shows; a
 * `cap`'d tab defers to the same `useCapability` check `editCap` uses).
 * This is a WHOLE-PAGE fold, not a per-tab one: while `editing`, `editView`
 * renders and the tab strip does not exist in the DOM at all — so the
 * existing discard guard, `beforeunload` listener, and
 * loading>notFound>error precedence need zero change, and `onTabChange` is
 * called directly (never routed through the discard guard), since there is
 * no tab-switch to guard while editing.
 */
export interface DetailPageTab {
  key: string;
  label: ReactNode;
  children: ReactNode;
  /** Gate for this tab. A tab with no `cap` always shows. */
  cap?: Capability;
}

export interface DetailPageProps {
  title: ReactNode;
  breadcrumbs?: Breadcrumb[];
  subtitle?: ReactNode;

  onNavigate?: (target: { href: string }) => void;
  onBack?: () => void;

  loading?: boolean;
  error?: unknown;
  onRetry?: () => void;
  /** Caller-derived: the useOne error was a 404. */
  notFound?: boolean;
  notFoundHref?: string;

  readView: ReactNode;
  /**
   * The edit form. Omit for a READ-ONLY detail page (e.g. user detail, whose
   * edit is backend-blocked): no Edit button renders and the page never enters
   * edit mode. When provided, `editing`/`onEditingChange` drive the toggle.
   */
  editView?: ReactNode;
  editing?: boolean;
  onEditingChange?: (next: boolean) => void;
  /** Gate for the Edit action. Denied → no Edit button. Ignored when read-only. */
  editCap?: Capability;

  /** Caller-derived from the edit form. Drives the discard guard. */
  dirty?: boolean;

  /** Extra actions (Archive/Restore, …), shown left of Edit in read mode. */
  actions?: ReactNode;

  /**
   * Read-branch tab region. When present, replaces `readView` with an antd
   * `<Tabs>` built from this list (cap-filtered); when absent, `readView`
   * renders exactly as it did before this prop existed. See the class-level
   * doc comment above for the whole-page-fold rationale.
   */
  tabs?: DetailPageTab[];
  /** Controlled active tab key. Ignored (and `tabs` unused) while editing. */
  activeTab?: string;
  /**
   * Called directly on a tab click — NOT routed through the discard guard.
   * Tabs are unmounted while editing, so there is never a tab-switch to
   * guard; the guard covers leaving the whole page, not switching tabs.
   */
  onTabChange?: (key: string) => void;
}

export function DetailPage({
  title,
  breadcrumbs,
  subtitle,
  onNavigate,
  onBack,
  loading,
  error,
  onRetry,
  notFound,
  notFoundHref,
  readView,
  editView,
  editing = false,
  onEditingChange,
  editCap,
  dirty,
  actions,
  tabs,
  activeTab,
  onTabChange,
}: DetailPageProps) {
  const confirm = useConfirmAction();
  const capAllowsEdit = useCapability(editCap);
  // A page is editable only when the caller supplied both an edit form and a
  // way to toggle it. A read-only page (user detail) omits both, so no Edit
  // affordance renders and `editing` can never flip true.
  const editable = editView != null && onEditingChange != null;
  const canEdit = capAllowsEdit && editable;
  // Not useCapability(t.cap) per tab: that hook is called unconditionally at
  // a component's top level, so calling it once per array element inside a
  // .map() (a variable-length loop) is the hooks-in-a-loop antipattern.
  // useCapability's own logic is two lines (cap undefined -> true; else
  // defer to the predicate) — reproduced inline below against the raw `can`
  // predicate, which is an ordinary function and fine to call in a loop.
  const { can } = useCapabilityContext();
  const guardActive = editing && Boolean(dirty);

  // Run `proceed` directly, or behind a discard confirmation when guarding.
  const guarded = useCallback(
    (proceed: () => void) => {
      if (!guardActive) {
        proceed();
        return;
      }
      confirm({
        title: 'Discard unsaved changes?',
        content: 'Your edits to this record will be lost.',
        okText: 'Discard',
        danger: true,
        onConfirm: async () => proceed(),
      });
    },
    [guardActive, confirm],
  );

  // Cover a true browser navigation (reload / close tab) while dirty.
  useEffect(() => {
    if (!guardActive) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [guardActive]);

  const guardedBack = onBack ? () => guarded(onBack) : undefined;
  const guardedNavigate = onNavigate
    ? (target: { href: string }) => guarded(() => onNavigate(target))
    : undefined;

  const headerActions = editing ? (
    <Button onClick={() => guarded(() => onEditingChange?.(false))}>Cancel</Button>
  ) : (
    <>
      {actions}
      {canEdit && (
        <Button type="primary" onClick={() => onEditingChange?.(true)}>
          Edit
        </Button>
      )}
    </>
  );

  // The header (breadcrumb/back/title) always renders so navigation stays
  // reachable, but the ACTION buttons operate on a record — Edit, the caller's
  // Archive/Restore, and the edit-mode Cancel. In the non-body states there is
  // no actionable record: `loading` shows a skeleton, `notFound` a 404, `error`
  // a failure. Rendering the actions there floats Edit/Archive over the
  // skeleton/404/error (and lets a user click Edit on a record that is still
  // loading or does not exist). Suppress them in exactly the states the body
  // precedence below owns; they return with the read/edit view.
  const showActions = !loading && !notFound && !error;

  const header = (
    <PageHeader
      title={title}
      breadcrumbs={breadcrumbs}
      subtitle={subtitle}
      actions={showActions ? headerActions : undefined}
      onBack={guardedBack}
      onNavigate={guardedNavigate}
    />
  );

  // Precedence: loading > notFound > error > body. Each branch feeds
  // AsyncBoundary only the one flag it owns, so AsyncBoundary's internal
  // error-before-loading order never has a chance to invert this one.
  let body: ReactNode;
  if (loading) {
    body = (
      <AsyncBoundary loading skeleton="form">
        {null}
      </AsyncBoundary>
    );
  } else if (notFound) {
    body = (
      <Result
        status="404"
        title="Not found"
        subTitle="This record doesn't exist or has been removed."
        extra={
          notFoundHref && onNavigate ? (
            <Button onClick={() => onNavigate({ href: notFoundHref })}>Back to list</Button>
          ) : undefined
        }
      />
    );
  } else if (error) {
    body = (
      <AsyncBoundary error={error} onRetry={onRetry} errorPresentation="page">
        {null}
      </AsyncBoundary>
    );
  } else if (editing && editView != null) {
    body = editView;
  } else if (tabs) {
    const visibleTabs = tabs
      .filter((t) => t.cap === undefined || can(t.cap))
      .map((t) => ({ key: t.key, label: t.label, children: t.children }));
    body = <Tabs activeKey={activeTab} onChange={onTabChange} items={visibleTabs} />;
  } else {
    body = readView;
  }

  return (
    <div style={{ padding: space.xl }}>
      {header}
      {body}
    </div>
  );
}
